import {
    ChangeEvent,
    KeyboardEvent,
    memo,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    keyframes,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import MemoryIcon from '@mui/icons-material/Memory';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import PanToolIcon from '@mui/icons-material/PanTool';
import BrushIcon from '@mui/icons-material/Brush';

import { SCUIButton, SCUILoadingLinear, SCUIPagination } from '../../../atomic/atoms';
import { SCUIBreadCrumb } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import {
    BatchProcessTrackingQuery,
    type BatchProcessTrackingDetail,
    type BatchProcessTrackingFilterParams,
    type BatchProcessTrackingFrame,
    type BatchProcessTrackingHistoryEntry,
    type BatchProcessTrackingItem,
    type BatchProcessTrackingMetrics,
} from '../../../services/studio/StudioQuery/BatchProcessTracking/BatchProcessTrackingQuery.services';
import { BatchProcessTrackingCommand } from '../../../services/studio/StudioCommand/BatchProcessTracking/BatchProcessTrackingCommand.services';
import { WorkAssignmentQuery } from '../../../services/studio/StudioQuery/WorkAssignment/WorkAssignmentQuery.services';
import type { WorkAssignmentCapabilities } from '../../../services/studio/StudioQuery/RawImageReview/RawImageReviewQuery.services';
import { GetS3PresignedDownloadUrls } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import { useStudioBatchTrackingHub } from '../../../services/signalr/useStudioBatchTrackingHub';
import type { StudioBatchItemUpdatedPayload } from '../../../services/signalr/types';
import { toTempS3Key } from '../../utils/studioShotS3Path';

const WORK_ASSIGNMENT_FEATURE_KEY = 'rawImageReview';

const canRestartLlmBatch = (detail: BatchProcessTrackingDetail | null) => {
    if (!detail?.item) {
        return false;
    }
    if (detail.item.aggregateStatus === 'LlmBatchProcessing') {
        return true;
    }
    if (detail.item.frames.some((frame) => frame.status === 'LlmBatchProcessing')) {
        return true;
    }
    return detail.batchJobs.some((job) => job.status === 'Processing' || job.status === 'Queued');
};

const REALTIME_REFRESH_DEBOUNCE_MS = 1000;

const PIPELINE_STATUSES = [
    'RawShotStarted',
    'RawShotCompleted',
    'BatchProcessing',
    'ReadyForLlmBatch',
    'LlmBatchProcessing',
    'BatchProcessCompleted',
    'AiOutputApproved',
    'ManualAIProcessRequired',
    'TransferredToPhotoshop',
] as const;

type StatusTone = { bg: string; color: string; border: string };

const STATUS_TONES: Record<string, StatusTone> = {
    RawShotStarted: { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
    RawShotCompleted: { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' },
    BatchProcessing: { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    ReadyForLlmBatch: { bg: '#eef2ff', color: '#4338ca', border: '#c7d2fe' },
    LlmBatchProcessing: { bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
    BatchProcessCompleted: { bg: '#ecfdf5', color: '#047857', border: '#a7f3d0' },
    AiOutputApproved: { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
    ManualAIProcessRequired: { bg: '#fef2f2', color: '#b91c1c', border: '#fecaca' },
    TransferredToPhotoshop: { bg: '#f0fdfa', color: '#0f766e', border: '#99f6e4' },
};

const DEFAULT_TONE = STATUS_TONES.RawShotCompleted;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;
const pulse = keyframes`
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.25); opacity: 0.65; }
`;
const sparkle = keyframes`
    0%, 100% { transform: scale(1) rotate(0deg); opacity: 1; }
    50% { transform: scale(1.3) rotate(20deg); opacity: 0.6; }
`;
const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    20% { transform: translateX(-2px) rotate(-8deg); }
    40% { transform: translateX(2px) rotate(8deg); }
    60% { transform: translateX(-2px) rotate(-8deg); }
    80% { transform: translateX(2px) rotate(8deg); }
`;
const pop = keyframes`
    0% { transform: scale(0.4); opacity: 0; }
    70% { transform: scale(1.2); opacity: 1; }
    100% { transform: scale(1); }
`;

type StatusVisual = { icon: ReactNode; animation?: string };

const STATUS_VISUALS: Record<string, StatusVisual> = {
    RawShotStarted: { icon: <AddAPhotoIcon />, animation: `${pulse} 1.6s ease-in-out infinite` },
    RawShotCompleted: { icon: <PhotoCameraIcon /> },
    BatchProcessing: { icon: <AutorenewIcon />, animation: `${spin} 1.4s linear infinite` },
    ReadyForLlmBatch: { icon: <SendIcon />, animation: `${pulse} 2s ease-in-out infinite` },
    LlmBatchProcessing: { icon: <AutoAwesomeIcon />, animation: `${sparkle} 1.5s ease-in-out infinite` },
    BatchProcessCompleted: { icon: <TaskAltIcon />, animation: `${pop} 0.5s ease-out 1` },
    AiOutputApproved: { icon: <VerifiedIcon />, animation: `${pop} 0.5s ease-out 1` },
    ManualAIProcessRequired: { icon: <PanToolIcon />, animation: `${shake} 1.2s ease-in-out infinite` },
    TransferredToPhotoshop: { icon: <BrushIcon />, animation: `${pop} 0.5s ease-out 1` },
};

const DEFAULT_VISUAL: StatusVisual = { icon: <HourglassTopIcon /> };

const EMPTY_METRICS: BatchProcessTrackingMetrics = {
    preProcessingCount: 0,
    aiProcessingCount: 0,
    completedTodayCount: 0,
    manualRequiredCount: 0,
};

const formatDate = (value?: string) => {
    if (!value) {
        return '-';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const resolveStatusLabel = (status: string | undefined, nameTr?: string) => {
    if (!status) {
        return '-';
    }
    // Öncelik: DB StatusDescription (nameTr); yoksa status kodu.
    return nameTr?.trim() || status;
};

const StatusBadge = ({ status, nameTr }: { status: string; nameTr?: string }) => {
    const tone = STATUS_TONES[status] ?? DEFAULT_TONE;
    const visual = STATUS_VISUALS[status] ?? DEFAULT_VISUAL;
    const label = resolveStatusLabel(status, nameTr);
    return (
        <Chip
            size="small"
            title={status}
            label={label}
            icon={
                <Box
                    component="span"
                    sx={{
                        display: 'inline-flex',
                        animation: visual.animation,
                        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                        '& svg': { fontSize: 16 },
                    }}
                >
                    {visual.icon}
                </Box>
            }
            sx={{
                fontWeight: 700,
                fontSize: 11,
                bgcolor: tone.bg,
                color: tone.color,
                border: `1px solid ${tone.border}`,
                '& .MuiChip-icon': { color: tone.color, ml: 0.75, mr: -0.25 },
            }}
        />
    );
};

type MetricCardProps = {
    label: string;
    value: number;
    hint: string;
    color: string;
    bg: string;
    icon: ReactNode;
};

const MetricCard = ({ label, value, hint, color, bg, icon }: MetricCardProps) => (
    <Paper
        variant="outlined"
        sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flex: '1 1 200px' }}
    >
        <Box minWidth={0}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {label}
            </Typography>
            <Typography variant="h5" fontWeight={900} sx={{ color }}>
                {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {hint}
            </Typography>
        </Box>
        <Box
            sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: bg,
                color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}
        >
            {icon}
        </Box>
    </Paper>
);

const COMPLETED_STATUS = 'BatchProcessCompleted';

const isFrameCompleted = (frame: BatchProcessTrackingFrame) => frame.status === COMPLETED_STATUS;

/** BatchProcessCompleted → llmThumbnail; aksi halde filePath. processedFilePath gösterilmez. */
const resolveDisplayKey = (frame: BatchProcessTrackingFrame) => {
    if (isFrameCompleted(frame)) {
        return frame.llmThumbnail || frame.llmProcessedFilePath || frame.filePath || '';
    }
    return frame.filePath || '';
};

/** Backend thumbnail key'i dönebilir ({ENV}/.thumbnail/...); indirme için gerçek orijinal key'e çevirir. */
const toOriginalS3Key = (key: string) => key.replace(/\\/g, '/').replace(/(^|\/)\.thumbnail\//i, '$1');

/** Orijinal indirme: tamamlandıysa LLM çıktısı, değilse ham filePath. */
const resolveDownloadKey = (frame: BatchProcessTrackingFrame) => {
    if (isFrameCompleted(frame)) {
        return toOriginalS3Key(frame.llmProcessedFilePath || frame.filePath || '');
    }
    return toOriginalS3Key(frame.filePath || '');
};

type ThumbnailProps = {
    frame: BatchProcessTrackingFrame;
    tempUrl?: string;
    originalUrl?: string;
};

const ThumbnailBase = ({ frame, tempUrl, originalUrl }: ThumbnailProps) => {
    const label = frame.photoTypeDisplayName || frame.photoTypeCode;
    const [src, setSrc] = useState(tempUrl || originalUrl);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setSrc(tempUrl || originalUrl);
        setFailed(false);
    }, [tempUrl, originalUrl]);

    return (
        <Box
            title={`${label} - ${frame.status}`}
            sx={{
                position: 'relative',
                width: 44,
                height: 44,
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                overflow: 'hidden',
                flexShrink: 0,
            }}
        >
            {src && !failed ? (
                <Box
                    component="img"
                    src={src}
                    alt={label}
                    loading="lazy"
                    decoding="async"
                    width={44}
                    height={44}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={() => {
                        if (tempUrl && originalUrl && src === tempUrl) {
                            setSrc(originalUrl);
                            return;
                        }
                        setFailed(true);
                    }}
                />
            ) : null}
        </Box>
    );
};

// Realtime yenilemelerde frame objesi yeniden oluşur; yalnızca görünümü etkileyen alanlar değişince render et.
const Thumbnail = memo(
    ThumbnailBase,
    (prev, next) =>
        prev.tempUrl === next.tempUrl &&
        prev.originalUrl === next.originalUrl &&
        prev.frame.rawImageId === next.frame.rawImageId &&
        prev.frame.status === next.frame.status &&
        prev.frame.photoTypeCode === next.frame.photoTypeCode,
);

export const BatchProcessTracking = () => {
    const { t } = useTranslation();

    const [items, setItems] = useState<BatchProcessTrackingItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [metrics, setMetrics] = useState<BatchProcessTrackingMetrics>(EMPTY_METRICS);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [barcodeInput, setBarcodeInput] = useState('');
    const [statusInput, setStatusInput] = useState('');
    const [createdByInput, setCreatedByInput] = useState('');
    const [filters, setFilters] = useState<BatchProcessTrackingFilterParams | null>(null);
    const [filterReady, setFilterReady] = useState(false);
    const [capabilities, setCapabilities] = useState<WorkAssignmentCapabilities | null>(null);
    const [createdByOptions, setCreatedByOptions] = useState<string[]>([]);
    const [statusNamesTr, setStatusNamesTr] = useState<Record<string, string>>({});
    const [statusFilterCodes, setStatusFilterCodes] = useState<string[]>([...PIPELINE_STATUSES]);
    const statusNamesTrRef = useRef<Record<string, string>>({});
    statusNamesTrRef.current = statusNamesTr;

    const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
    const imageUrlsRef = useRef<Record<string, string>>({});
    const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());

    const [detailBarcode, setDetailBarcode] = useState<string | null>(null);
    const [detail, setDetail] = useState<BatchProcessTrackingDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [history, setHistory] = useState<BatchProcessTrackingHistoryEntry[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [historyError, setHistoryError] = useState(false);
    const [restartingLlmBatch, setRestartingLlmBatch] = useState(false);
    const historyRequestIdRef = useRef(0);

    const canUseUnrestrictedFilter = capabilities?.canUseUnrestrictedFilter === true;
    const currentUser = capabilities?.currentAssignee?.trim() || '';

    // Seçili değer her zaman MenuItem listesinde olsun (yoksa MUI Select boş render eder).
    const createdBySelectOptions = useMemo(() => {
        const users = new Set<string>();
        const add = (value?: string) => {
            const trimmed = value?.trim();
            if (trimmed) {
                users.add(trimmed);
            }
        };
        createdByOptions.forEach(add);
        add(currentUser);
        add(createdByInput);
        return Array.from(users).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }, [createdByOptions, currentUser, createdByInput]);

    const createdBySelectValue = useMemo(() => {
        if (!createdByInput) {
            return '';
        }
        const exact = createdBySelectOptions.find((user) => user === createdByInput);
        if (exact) {
            return exact;
        }
        const matched = createdBySelectOptions.find(
            (user) => user.toLocaleLowerCase('tr-TR') === createdByInput.toLocaleLowerCase('tr-TR'),
        );
        return matched ?? createdByInput;
    }, [createdByInput, createdBySelectOptions]);

    // Latest query state for realtime callbacks (avoids stale closures inside the SignalR handlers).
    const queryRef = useRef({ page, rowsPerPage, filters });
    queryRef.current = { page, rowsPerPage, filters };
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadPresignedUrls = useCallback(async (list: BatchProcessTrackingItem[]) => {
        const keys = new Set<string>();
        list.flatMap((item) => item.frames).forEach((frame) => {
            const source = resolveDisplayKey(frame);
            if (!source) {
                return;
            }
            // Backend çoğu zaman zaten .thumbnail key döner; yine de türetmeyi dene.
            const tempKey = toTempS3Key(source);
            if (!imageUrlsRef.current[tempKey]) {
                keys.add(tempKey);
            }
            if (tempKey !== source && !imageUrlsRef.current[source]) {
                keys.add(source);
            }
        });
        if (keys.size === 0) {
            return;
        }

        try {
            const presigned = await GetS3PresignedDownloadUrls(Array.from(keys));
            const next: Record<string, string> = {};
            presigned.items.forEach((entry) => {
                if (entry.key && entry.url) {
                    next[entry.key] = entry.url;
                }
            });
            imageUrlsRef.current = { ...imageUrlsRef.current, ...next };
            setImageUrls({ ...imageUrlsRef.current });
        } catch {
            // thumbnails are optional
        }
    }, []);

    const fetchData = useCallback(
        async (silent: boolean) => {
            const { page: currentPage, rowsPerPage: currentRows, filters: currentFilters } = queryRef.current;
            if (!currentFilters) {
                return;
            }
            if (!silent) {
                setLoading(true);
            }
            try {
                const [list, metricsResponse] = await Promise.all([
                    BatchProcessTrackingQuery.GetItems({
                        ...currentFilters,
                        pageNumber: currentPage + 1,
                        pageSize: currentRows,
                    }),
                    BatchProcessTrackingQuery.GetMetrics(currentFilters),
                ]);
                setItems(list.items);
                setStatusNamesTr((current) => {
                    const next = { ...current };
                    list.items.forEach((item) => {
                        if (item.aggregateStatus && item.aggregateStatusNameTr) {
                            next[item.aggregateStatus] = item.aggregateStatusNameTr;
                        }
                        item.frames.forEach((frame) => {
                            if (frame.status && frame.statusNameTr) {
                                next[frame.status] = frame.statusNameTr;
                            }
                        });
                    });
                    return next;
                });
                setTotalCount(list.totalCount);
                setMetrics(metricsResponse);
                // Presign kritik yolda beklenmez: liste hemen render olur, görseller sonradan gelir.
                void loadPresignedUrls(list.items);
            } catch {
                if (!silent) {
                    setItems([]);
                    setTotalCount(0);
                    Swal.fire({ icon: 'error', title: t('studio.batchProcessTracking.loadError') }).catch(
                        () => undefined,
                    );
                }
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [loadPresignedUrls, t],
    );

    useEffect(() => {
        let cancelled = false;
        void WorkAssignmentQuery.GetContext(WORK_ASSIGNMENT_FEATURE_KEY)
            .then((context) => {
                if (cancelled) {
                    return;
                }
                const username = context.currentAssignee?.trim() || '';
                setCapabilities(context);
                setCreatedByInput(username);
                setFilters(username ? { createdBy: username } : {});
                setFilterReady(true);
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                setCapabilities(null);
                setCreatedByInput('');
                setFilters({});
                setFilterReady(true);
                Swal.fire({ icon: 'error', title: t('studio.batchProcessTracking.loadError') }).catch(
                    () => undefined,
                );
            });
        return () => {
            cancelled = true;
        };
    }, [t]);

    useEffect(() => {
        if (!filterReady || !filters) {
            return;
        }
        void fetchData(false);
    }, [fetchData, page, rowsPerPage, filters, filterReady]);

    useEffect(() => {
        if (!filterReady) {
            return;
        }
        void BatchProcessTrackingQuery.GetCreatedByOptions()
            .then(setCreatedByOptions)
            .catch(() => setCreatedByOptions([]));
        void BatchProcessTrackingQuery.GetStatusOptions()
            .then((options) => {
                if (!options.length) {
                    return;
                }
                const map: Record<string, string> = {};
                options.forEach((option) => {
                    if (option.statusName) {
                        map[option.statusName] = option.statusDescription || option.statusName;
                    }
                });
                setStatusNamesTr((current) => ({ ...current, ...map }));
                setStatusFilterCodes(options.map((option) => option.statusName).filter(Boolean));
            })
            .catch(() => undefined);
    }, [filterReady]);

    useEffect(
        () => () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        },
        [],
    );

    const scheduleRefresh = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }
        refreshTimerRef.current = setTimeout(() => {
            refreshTimerRef.current = null;
            void fetchData(true);
        }, REALTIME_REFRESH_DEBOUNCE_MS);
    }, [fetchData]);

    const handleItemUpdated = useCallback(
        (payload: StudioBatchItemUpdatedPayload) => {
            // Instant visual feedback for visible rows; the debounced refetch then brings frames/thumbnails in sync.
            const barcode = payload.barcode?.trim();
            if (!barcode) {
                scheduleRefresh();
                return;
            }

            setItems((current) =>
                current.map((item) => {
                    if (item.barcode?.trim().toLowerCase() !== barcode.toLowerCase()) {
                        return item;
                    }
                    const nextStatus = payload.aggregateStatus ?? item.aggregateStatus;
                    const statusChanged =
                        Boolean(payload.aggregateStatus)
                        && payload.aggregateStatus !== item.aggregateStatus;
                    return {
                        ...item,
                        aggregateStatus: nextStatus,
                        aggregateStatusNameTr: statusChanged
                            ? statusNamesTrRef.current[nextStatus] || ''
                            : item.aggregateStatusNameTr,
                        createdBy: payload.createdBy ?? item.createdBy,
                        assignee: payload.assignee ?? item.assignee,
                        lastUpdatedAt: payload.updatedAtUtc || item.lastUpdatedAt,
                    };
                }),
            );
            scheduleRefresh();
        },
        [scheduleRefresh],
    );

    useStudioBatchTrackingHub({
        onItemUpdated: handleItemUpdated,
        onRefresh: scheduleRefresh,
        onReconnected: () => void fetchData(true),
    });

    useEffect(() => {
        historyRequestIdRef.current += 1;
        setHistoryExpanded(false);
        setHistory([]);
        setHistoryLoaded(false);
        setHistoryError(false);
        setHistoryLoading(false);

        if (!detailBarcode) {
            setDetail(null);
            return;
        }
        let cancelled = false;
        setDetailLoading(true);
        BatchProcessTrackingQuery.GetDetail(detailBarcode)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                setDetail(response);
                if (response.item) {
                    void loadPresignedUrls([response.item]);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDetail(null);
                    Swal.fire({ icon: 'error', title: t('studio.batchProcessTracking.detailError') }).catch(
                        () => undefined,
                    );
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setDetailLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [detailBarcode, loadPresignedUrls, t]);

    const loadHistory = useCallback((barcode: string) => {
        const requestId = ++historyRequestIdRef.current;
        setHistoryLoading(true);
        setHistoryError(false);
        BatchProcessTrackingQuery.GetHistory(barcode)
            .then((entries) => {
                if (requestId !== historyRequestIdRef.current) {
                    return;
                }
                setHistory(entries);
                setHistoryLoaded(true);
            })
            .catch(() => {
                if (requestId !== historyRequestIdRef.current) {
                    return;
                }
                setHistoryError(true);
                setHistoryLoaded(false);
            })
            .finally(() => {
                if (requestId !== historyRequestIdRef.current) {
                    return;
                }
                setHistoryLoading(false);
            });
    }, []);

    const handleHistoryAccordionChange = (_: unknown, expanded: boolean) => {
        setHistoryExpanded(expanded);
        if (!expanded || !detailBarcode || historyLoaded || historyLoading) {
            return;
        }
        loadHistory(detailBarcode);
    };

    const reloadDetail = useCallback(
        (barcode: string) => {
            setDetailLoading(true);
            BatchProcessTrackingQuery.GetDetail(barcode)
                .then((response) => {
                    setDetail(response);
                    if (response.item) {
                        void loadPresignedUrls([response.item]);
                    }
                })
                .catch(() => {
                    Swal.fire({ icon: 'error', title: t('studio.batchProcessTracking.detailError') }).catch(
                        () => undefined,
                    );
                })
                .finally(() => setDetailLoading(false));
        },
        [loadPresignedUrls, t],
    );

    const handleRestartLlmBatch = async () => {
        if (!detailBarcode || restartingLlmBatch) {
            return;
        }

        const confirm = await Swal.fire({
            icon: 'warning',
            title: t('studio.batchProcessTracking.restartLlmBatchConfirmTitle'),
            text: t('studio.batchProcessTracking.restartLlmBatchConfirmText'),
            showCancelButton: true,
            confirmButtonText: t('studio.batchProcessTracking.restartLlmBatchConfirm'),
            cancelButtonText: t('studio.batchProcessTracking.restartLlmBatchCancel'),
        });
        if (!confirm.isConfirmed) {
            return;
        }

        setRestartingLlmBatch(true);
        try {
            const result = await BatchProcessTrackingCommand.RestartLlmBatch(detailBarcode);
            if (result.status === 'Completed') {
                await Swal.fire({
                    icon: 'success',
                    title: t('studio.batchProcessTracking.restartLlmBatchSuccess'),
                    text: result.message,
                });
                historyRequestIdRef.current += 1;
                setHistoryExpanded(false);
                setHistory([]);
                setHistoryLoaded(false);
                setHistoryError(false);
                reloadDetail(detailBarcode);
                return;
            }

            const title =
                result.status === 'DispatchInFlight'
                    ? t('studio.batchProcessTracking.restartLlmBatchInFlight')
                    : t('studio.batchProcessTracking.restartLlmBatchError');
            await Swal.fire({
                icon: result.status === 'DispatchInFlight' ? 'info' : 'error',
                title,
                text: result.message,
            });
        } catch {
            await Swal.fire({
                icon: 'error',
                title: t('studio.batchProcessTracking.restartLlmBatchError'),
            });
        } finally {
            setRestartingLlmBatch(false);
        }
    };

    const applyFilters = () => {
        const createdBy = canUseUnrestrictedFilter
            ? createdByInput || undefined
            : currentUser || undefined;
        setPage(0);
        setFilters({
            barcode: barcodeInput.trim() || undefined,
            status: statusInput || undefined,
            createdBy,
        });
    };

    const resetFilters = () => {
        setBarcodeInput('');
        setStatusInput('');
        const createdBy = canUseUnrestrictedFilter ? '' : currentUser;
        setCreatedByInput(createdBy);
        setPage(0);
        setFilters(createdBy ? { createdBy } : {});
    };

    const handleChangeRows = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    const handleBarcodeKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    const urlsOfKey = (source: string) => {
        if (!source) {
            return { tempUrl: undefined, originalUrl: undefined };
        }
        return {
            tempUrl: imageUrls[toTempS3Key(source)],
            originalUrl: imageUrls[source],
        };
    };
    const urlsOf = (frame: BatchProcessTrackingFrame) => urlsOfKey(resolveDisplayKey(frame));

    const downloadKey = async (s3Key: string) => {
        let url = imageUrlsRef.current[s3Key];
        if (!url) {
            const presigned = await GetS3PresignedDownloadUrls([s3Key]);
            url = presigned.items?.[0]?.url ?? '';
            if (url) {
                imageUrlsRef.current = { ...imageUrlsRef.current, [s3Key]: url };
            }
        }
        if (!url) {
            throw new Error('presigned url missing');
        }
        const fileName = s3Key.split('/').pop() || 'original_image.jpg';
        const triggerDownload = (href: string) => {
            const link = document.createElement('a');
            link.href = href;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`download failed: ${response.status}`);
            }
            const blobUrl = window.URL.createObjectURL(await response.blob());
            triggerDownload(blobUrl);
            window.URL.revokeObjectURL(blobUrl);
        } catch {
            // CORS vb. nedenle blob alınamazsa doğrudan presigned URL'e düş.
            triggerDownload(url);
        }
    };

    const handleDownload = async (busyId: string, frames: BatchProcessTrackingFrame[]) => {
        const keys = Array.from(new Set(frames.map(resolveDownloadKey).filter(Boolean)));
        if (keys.length === 0 || downloadingIds.has(busyId)) {
            return;
        }
        setDownloadingIds((current) => new Set(current).add(busyId));
        try {
            for (const key of keys) {
                await downloadKey(key);
            }
        } catch {
            Swal.fire({ icon: 'error', title: t('studio.batchProcessTracking.downloadError') }).catch(() => undefined);
        } finally {
            setDownloadingIds((current) => {
                const next = new Set(current);
                next.delete(busyId);
                return next;
            });
        }
    };

    const renderDownloadButton = (busyId: string, frames: BatchProcessTrackingFrame[]) => {
        const busy = downloadingIds.has(busyId);
        return (
            <IconButton
                size="small"
                disabled={busy}
                aria-label={t('studio.batchProcessTracking.download')}
                title={t('studio.batchProcessTracking.download')}
                onClick={() => void handleDownload(busyId, frames)}
            >
                {busy ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
            </IconButton>
        );
    };

    return (
        <SCUIContent>
            <SCUIBreadCrumb />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box minWidth={0}>
                    <Typography variant="h5" fontWeight={900}>
                        {t('studio.batchProcessTracking.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('studio.batchProcessTracking.subtitle')}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <MetricCard
                        label={t('studio.batchProcessTracking.metricPreProcessing')}
                        value={metrics.preProcessingCount}
                        hint={t('studio.batchProcessTracking.metricPreProcessingHint')}
                        color="#2563eb"
                        bg="#eff6ff"
                        icon={<HourglassTopIcon />}
                    />
                    <MetricCard
                        label={t('studio.batchProcessTracking.metricAiProcessing')}
                        value={metrics.aiProcessingCount}
                        hint={t('studio.batchProcessTracking.metricAiProcessingHint')}
                        color="#d97706"
                        bg="#fffbeb"
                        icon={<MemoryIcon />}
                    />
                    <MetricCard
                        label={t('studio.batchProcessTracking.metricCompleted')}
                        value={metrics.completedTodayCount}
                        hint={t('studio.batchProcessTracking.metricCompletedHint')}
                        color="#059669"
                        bg="#ecfdf5"
                        icon={<CheckCircleOutlineIcon />}
                    />
                    <MetricCard
                        label={t('studio.batchProcessTracking.metricManual')}
                        value={metrics.manualRequiredCount}
                        hint={t('studio.batchProcessTracking.metricManualHint')}
                        color="#dc2626"
                        bg="#fef2f2"
                        icon={<WarningAmberIcon />}
                    />
                </Stack>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', md: 'center' }}
                        flexWrap="wrap"
                        useFlexGap
                    >
                        <TextField
                            size="small"
                            sx={{ minWidth: 260 }}
                            value={barcodeInput}
                            onChange={(event) => setBarcodeInput(event.target.value)}
                            onKeyDown={handleBarcodeKeyDown}
                            placeholder={t('studio.batchProcessTracking.barcodePlaceholder')}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel shrink>{t('studio.batchProcessTracking.statusFilter')}</InputLabel>
                            <Select
                                label={t('studio.batchProcessTracking.statusFilter')}
                                value={statusInput}
                                displayEmpty
                                renderValue={(selected) => {
                                    if (!selected) {
                                        return t('studio.batchProcessTracking.allStatuses');
                                    }
                                    const code = String(selected);
                                    return resolveStatusLabel(code, statusNamesTr[code]);
                                }}
                                onChange={(event) => setStatusInput(String(event.target.value))}
                            >
                                <MenuItem value="">{t('studio.batchProcessTracking.allStatuses')}</MenuItem>
                                {statusFilterCodes.map((status) => (
                                    <MenuItem key={status} value={status}>
                                        <StatusBadge status={status} nameTr={statusNamesTr[status]} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 220 }} disabled={!canUseUnrestrictedFilter}>
                            <InputLabel shrink>{t('studio.batchProcessTracking.createdByFilter')}</InputLabel>
                            <Select
                                label={t('studio.batchProcessTracking.createdByFilter')}
                                value={createdBySelectValue}
                                displayEmpty
                                renderValue={(selected) =>
                                    selected
                                        ? String(selected)
                                        : t('studio.batchProcessTracking.allUsers')
                                }
                                onChange={(event) => {
                                    const nextUser = String(event.target.value);
                                    setCreatedByInput(nextUser);
                                    if (!canUseUnrestrictedFilter) {
                                        return;
                                    }
                                    setPage(0);
                                    setFilters({
                                        barcode: barcodeInput.trim() || undefined,
                                        status: statusInput || undefined,
                                        createdBy: nextUser || undefined,
                                    });
                                }}
                                disabled={!canUseUnrestrictedFilter}
                            >
                                {canUseUnrestrictedFilter ? (
                                    <MenuItem value="">{t('studio.batchProcessTracking.allUsers')}</MenuItem>
                                ) : null}
                                {createdBySelectOptions.map((user) => (
                                    <MenuItem key={user} value={user}>
                                        {user}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="m"
                            text={t('studio.batchProcessTracking.applyFilters')}
                            showStartIcon
                            startIcon={<FilterAltIcon />}
                            onClick={applyFilters}
                        />
                        <SCUIButton
                            type="ghost"
                            sizeType="default"
                            size="m"
                            text={t('studio.batchProcessTracking.resetFilters')}
                            onClick={resetFilters}
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ ml: { md: 'auto' } }}>
                            {t('studio.batchProcessTracking.totalRecords', { count: totalCount })}
                        </Typography>
                    </Stack>
                </Paper>

                {loading ? <SCUILoadingLinear /> : null}

                <Paper variant="outlined">
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('studio.batchProcessTracking.colProduct')}</TableCell>
                                    <TableCell>{t('studio.batchProcessTracking.colBarcode')}</TableCell>
                                    <TableCell>{t('studio.batchProcessTracking.colColor')}</TableCell>
                                    <TableCell align="center">{t('studio.batchProcessTracking.colFrames')}</TableCell>
                                    <TableCell>{t('studio.batchProcessTracking.colStatus')}</TableCell>
                                    <TableCell>{t('studio.batchProcessTracking.colCreatedBy')}</TableCell>
                                    <TableCell>{t('studio.batchProcessTracking.assignee')}</TableCell>
                                    <TableCell align="right">{t('studio.batchProcessTracking.colActions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading && items.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                                            <Typography color="text.secondary">
                                                {t('studio.batchProcessTracking.noItems')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : null}
                                {items.map((item) => (
                                    <TableRow key={item.barcode} hover>
                                        <TableCell>
                                            <Typography fontWeight={700} variant="body2">
                                                {item.productName || '-'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {item.productClassification || ''}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace' }}>
                                            <Typography fontWeight={700} variant="body2" fontFamily="inherit">
                                                {item.barcode}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" fontFamily="inherit">
                                                {item.specialCode || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {item.colorCode
                                                ? `${item.colorCode}${item.colorName ? ` (${item.colorName})` : ''}`
                                                : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.75} justifyContent="center">
                                                {item.frames.map((frame) => (
                                                    <Thumbnail
                                                        key={frame.rawImageId}
                                                        frame={frame}
                                                        {...urlsOf(frame)}
                                                    />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <StatusBadge
                                                    status={item.aggregateStatus}
                                                    nameTr={
                                                        item.aggregateStatusNameTr
                                                        || statusNamesTr[item.aggregateStatus]
                                                    }
                                                />
                                                {item.aggregateStatus === COMPLETED_STATUS
                                                    ? renderDownloadButton(
                                                          `item:${item.barcode}`,
                                                          item.frames.filter((f) => f.status === COMPLETED_STATUS),
                                                      )
                                                    : null}
                                            </Stack>
                                        </TableCell>
                                        <TableCell>{item.createdBy || '-'}</TableCell>
                                        <TableCell>{item.assignee || '-'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                aria-label={t('studio.batchProcessTracking.detail')}
                                                onClick={() => setDetailBarcode(item.barcode)}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <SCUIPagination
                                rowsPerPageOptions={[10, 20, 50]}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(_: unknown, newPage: number) => setPage(newPage)}
                                onRowsPerPageChange={handleChangeRows}
                                setPage={setPage}
                            />
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>

            <Dialog open={Boolean(detailBarcode)} onClose={() => setDetailBarcode(null)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {t('studio.batchProcessTracking.detailTitle')}
                    <IconButton onClick={() => setDetailBarcode(null)} aria-label={t('studio.batchProcessTracking.close')}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading ? <SCUILoadingLinear /> : null}
                    {!detailLoading && !detail?.item ? (
                        <Typography color="text.secondary">{t('studio.batchProcessTracking.noItems')}</Typography>
                    ) : null}
                    {detail?.item ? (
                        <Stack spacing={2.5}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                    gap: 1.5,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: 'grey.50',
                                    border: 1,
                                    borderColor: 'divider',
                                }}
                            >
                                <DetailField label={t('studio.batchProcessTracking.productName')} value={detail.item.productName} />
                                <DetailField label={t('studio.batchProcessTracking.colBarcode')} value={detail.item.barcode} mono />
                                <DetailField
                                    label={t('studio.batchProcessTracking.specialColor')}
                                    value={`${detail.item.specialCode || '-'} - ${detail.item.colorCode || '-'}`}
                                />
                                <DetailField
                                    label={t('studio.batchProcessTracking.lastUpdated')}
                                    value={formatDate(detail.item.lastUpdatedAt)}
                                />
                                <DetailField label={t('studio.batchProcessTracking.colCreatedBy')} value={detail.item.createdBy} />
                                <DetailField label={t('studio.batchProcessTracking.assignee')} value={detail.item.assignee} />
                                <DetailField label={t('studio.batchProcessTracking.externalBatchId')} value={detail.item.externalBatchId} mono />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {t('studio.batchProcessTracking.colStatus')}
                                    </Typography>
                                    <StatusBadge
                                        status={detail.item.aggregateStatus}
                                        nameTr={
                                            detail.item.aggregateStatusNameTr
                                            || statusNamesTr[detail.item.aggregateStatus]
                                        }
                                    />
                                </Box>
                            </Box>

                            <Box>
                                <Typography variant="overline" color="text.secondary" fontWeight={700}>
                                    {t('studio.batchProcessTracking.frameStatuses')}
                                </Typography>
                                <Stack spacing={1}>
                                    {detail.item.frames.map((frame) => (
                                        <Stack
                                            key={frame.rawImageId}
                                            direction="row"
                                            alignItems="center"
                                            spacing={1.5}
                                            sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
                                        >
                                            <Thumbnail frame={frame} {...urlsOf(frame)} />
                                            <Typography variant="body2" fontWeight={600} flex={1} minWidth={0}>
                                                {frame.photoTypeDisplayName || frame.photoTypeCode}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatDate(frame.lastUpdatedAt)}
                                            </Typography>
                                            <StatusBadge
                                                status={frame.status}
                                                nameTr={frame.statusNameTr || statusNamesTr[frame.status]}
                                            />
                                            {frame.status === COMPLETED_STATUS
                                                ? renderDownloadButton(`frame:${frame.rawImageId}`, [frame])
                                                : null}
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>

                            {detail.batchJobs.length > 0 ? (
                                <Box>
                                    <Typography variant="overline" color="text.secondary" fontWeight={700}>
                                        {t('studio.batchProcessTracking.batchJobs')}
                                    </Typography>
                                    <Stack spacing={1}>
                                        {detail.batchJobs.map((job) => (
                                            <Box
                                                key={`${job.externalBatchId}-${job.requestDate}`}
                                                sx={{ p: 1, borderRadius: 1, border: 1, borderColor: 'divider' }}
                                            >
                                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                                    <Typography variant="body2" fontFamily="monospace" noWrap title={job.externalBatchId}>
                                                        {job.externalBatchId}
                                                    </Typography>
                                                    <Typography variant="caption" fontWeight={700}>
                                                        {job.status}
                                                    </Typography>
                                                </Stack>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDate(job.requestDate)}
                                                    {job.retryCount > 0 ? ` - ${t('studio.batchProcessTracking.retry', { count: job.retryCount })}` : ''}
                                                </Typography>
                                                {job.errorMessage ? (
                                                    <Typography variant="caption" color="error" display="block">
                                                        {job.errorMessage}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ))}
                                    </Stack>
                                </Box>
                            ) : null}

                            <Accordion
                                disableGutters
                                elevation={0}
                                expanded={historyExpanded}
                                onChange={handleHistoryAccordionChange}
                                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box>
                                        <Typography variant="overline" color="text.secondary" fontWeight={700} display="block" lineHeight={1.2}>
                                            {t('studio.batchProcessTracking.history')}
                                        </Typography>
                                        {!historyLoaded && !historyLoading ? (
                                            <Typography variant="caption" color="text.secondary">
                                                {t('studio.batchProcessTracking.historyHint')}
                                            </Typography>
                                        ) : null}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {historyLoading ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <CircularProgress size={18} />
                                            <Typography variant="body2" color="text.secondary">
                                                {t('studio.batchProcessTracking.historyLoading')}
                                            </Typography>
                                        </Stack>
                                    ) : null}
                                    {historyError ? (
                                        <Stack spacing={1}>
                                            <Typography variant="body2" color="error">
                                                {t('studio.batchProcessTracking.historyError')}
                                            </Typography>
                                            <SCUIButton
                                                type="secondary"
                                                sizeType="default"
                                                size="s"
                                                text={t('studio.batchProcessTracking.refresh')}
                                                onClick={() => detailBarcode && loadHistory(detailBarcode)}
                                            />
                                        </Stack>
                                    ) : null}
                                    {!historyLoading && !historyError && historyLoaded && history.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            {t('studio.batchProcessTracking.noHistory')}
                                        </Typography>
                                    ) : null}
                                    {!historyLoading && !historyError && history.length > 0 ? (
                                        <TableContainer sx={{ maxHeight: 320 }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>{t('studio.batchProcessTracking.historyColDate')}</TableCell>
                                                        <TableCell>{t('studio.batchProcessTracking.historyColOldStatus')}</TableCell>
                                                        <TableCell>{t('studio.batchProcessTracking.historyColNewStatus')}</TableCell>
                                                        <TableCell>{t('studio.batchProcessTracking.historyColUser')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {history.map((entry, index) => (
                                                        <TableRow key={`${entry.rawImageId}-${entry.createDate}-${index}`}>
                                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                                {formatDate(entry.createDate)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {entry.oldStatus ? (
                                                                    <StatusBadge
                                                                        status={entry.oldStatus}
                                                                        nameTr={
                                                                            entry.oldStatusNameTr
                                                                            || statusNamesTr[entry.oldStatus]
                                                                        }
                                                                    />
                                                                ) : (
                                                                    '-'
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {entry.newStatus ? (
                                                                    <StatusBadge
                                                                        status={entry.newStatus}
                                                                        nameTr={
                                                                            entry.newStatusNameTr
                                                                            || statusNamesTr[entry.newStatus]
                                                                        }
                                                                    />
                                                                ) : (
                                                                    '-'
                                                                )}
                                                            </TableCell>
                                                            <TableCell>{entry.createdBy || '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    ) : null}
                                </AccordionDetails>
                            </Accordion>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    {canRestartLlmBatch(detail) ? (
                        <SCUIButton
                            type="secondary"
                            sizeType="default"
                            size="m"
                            text={t('studio.batchProcessTracking.restartLlmBatch')}
                            onClick={() => void handleRestartLlmBatch()}
                            disabled={restartingLlmBatch || detailLoading}
                        />
                    ) : (
                        <Box />
                    )}
                    <SCUIButton
                        type="primary"
                        sizeType="default"
                        size="m"
                        text={t('studio.batchProcessTracking.close')}
                        onClick={() => setDetailBarcode(null)}
                    />
                </DialogActions>
            </Dialog>
        </SCUIContent>
    );
};

const DetailField = ({ label, value, mono }: { label: string; value?: string; mono?: boolean }) => (
    <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary" display="block">
            {label}
        </Typography>
        <Typography variant="body2" fontWeight={700} fontFamily={mono ? 'monospace' : undefined} noWrap title={value}>
            {value || '-'}
        </Typography>
    </Box>
);
